"use client";

/**
 * Live Prices on Sign-In (product, 2026-08-24): the auto full build for a stale
 * comparison page (/c/<slug>), IN PLACE.
 *
 * Requirement R5(a), owner-confirmed 2026-08-24: a SIGNED-IN visitor landing on
 * a STALE (>24h) product page auto-runs the full live search, watching the
 * normal gradual comparison build, and ends on the fresh comparison. A
 * signed-out visitor who signs in via the ribbon flips isSignedIn and this
 * fires the same way, so the ribbon needs no navigation of its own.
 *
 * 2026-08-24 no-blink rework: the first version covered the page with the
 * full-screen scene and router.replace()d to /compare - the cross-route swap
 * (cover unmounts, /compare mounts) produced a visible blink no transition can
 * smooth. Now the build runs RIGHT HERE: this wrapper swaps the stale page
 * content for <ComparisonStream/> (the exact component /compare renders), which
 * shows the standard gradual build in flow under the global header and, on
 * completion, keeps rendering the built comparison. ComparisonStream's own
 * completion redirect skips when the canonical target IS the current path
 * (ComparisonStream.tsx maybeRedirectToCanonicalSlug), so the flow ends where
 * it started with zero navigations - nothing can blink.
 *
 * Flash guard: Clerk takes ~0.5-2s to boot, and waiting for isLoaded would
 * flash the stale page first. Clerk's __client_uat cookie (unix ts > 0 while a
 * session exists) is readable at hydration, so a stale page is covered by a
 * neutral in-flow surface immediately; the PAID search still starts only after
 * useUser() verifies the session. A wrong hint (stale cookie, signed out)
 * simply uncovers back to the page + ribbon.
 *
 * The stream query is the page's clean identity (brand + model_number ->
 * product_label), never the stored raw_query (the "jibberish" blocker).
 * StaleCeilingRefresh keeps the very-stale handoff when its path is active;
 * this defers to it. Signed-in only (the money rule); inert while
 * NEXT_PUBLIC_LIVE_PRICES_ENABLED is off.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useUser } from "@clerk/nextjs";

import {
  ComparisonStream,
  type ComparisonSeed,
} from "@/components/comparison/ComparisonStream";
import { acquireBuildLock } from "@/lib/buildLock";
import { LIVE_PRICES_ENABLED, SEARCH_SCENE_ENABLED } from "@/lib/flags";

/** Hydration-time signed-in HINT, readable synchronously long before Clerk
 *  finishes booting. UI-cover only - the PAID stream always waits for the
 *  verified useUser() state. */
function clerkSessionHint(): boolean {
  try {
    const m = document.cookie.match(/(?:^|;\s*)__client_uat=(\d+)/);
    return !!m && Number(m[1]) > 0;
  } catch {
    return false;
  }
}


interface Props {
  /** The clean rebuild query (brand + model_number, falling back to
   *  product_label) - NEVER the page's stored raw_query, which can be junk
   *  text from whichever historical search published the page (the 2026-08-24
   *  "jibberish" blocker). Derived once in /c/[slug]/page.tsx. */
  rawQuery: string;
  /** 2026-09-20: the page's own slug, handed to the search so its tray row
   *  is the page's row (one product, one entry). */
  pageSlug?: string | null;
  isStale: boolean;
  isVeryStale: boolean;
  /** The page's pricing basis (the pSEO canonical postcode). */
  postcode: string;
  /** 2026-08-28 seeded in-place refresh: the page's SAVED offers + hero
   *  image. When present, the build swap is INVISIBLE - ComparisonStream
   *  starts pre-filled with the exact rows/photo the visitor is already
   *  looking at (each wearing a "Checking price" chip) and fresh prices
   *  replace them in place. Absent = the pre-2026-08-28 empty-shell swap. */
  seed?: ComparisonSeed;
  /** B-240 step 3: the one-card switch (server-resolved), forwarded to the
   *  live rebuild so signed-in and signed-out visitors see one behaviour. */
  oneCard?: boolean;
  /** The server-rendered comparison page. Rendered untouched unless a
   *  signed-in visitor on a stale page activates the build (then swapped). */
  children: ReactNode;
}

export function ProductLiveBuild({
  rawQuery,
  pageSlug = null,
  isStale,
  isVeryStale,
  postcode,
  seed,
  oneCard = false,
  children,
}: Props) {
  const { isSignedIn, isLoaded, user } = useUser();
  const [active, setActive] = useState(false);
  const [attachOnly, setAttachOnly] = useState(false);
  const [hintCover, setHintCover] = useState(false);
  const started = useRef(false);

  // 2026-09-14 (owner-caught loop): the page's rebuild query can change
  // spelling between renders (the publish rewrote the label with an
  // accent: "Renergie" -> "Rénergie"); keyed on `q`, ComparisonStream
  // remounted and started a SECOND paid search. The query is frozen at
  // first mount for the life of this component.
  const frozenQuery = useRef((rawQuery ?? "").trim());
  const q = frozenQuery.current;
  // StaleCeilingRefresh owns the very-stale handoff when its path is active;
  // never race it with a second build.
  const ceilingActive = SEARCH_SCENE_ENABLED && isVeryStale;
  const eligible =
    LIVE_PRICES_ENABLED && isStale && !ceilingActive && q.length > 0;

  // Before the next paint after hydration: cover a stale page when the session
  // cookie says this is (very likely) a signed-in visitor.
  useLayoutEffect(() => {
    // 2026-08-28: with a seed the swap is invisible (the stream starts
    // pre-filled with this exact page), so the dim-cover hint is
    // unnecessary - the page stays fully readable throughout.
    if (eligible && !seed && clerkSessionHint()) setHintCover(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clerk confirmed signed OUT: the hint was stale, uncover (ribbon flow).
    if (isLoaded && !isSignedIn && hintCover) setHintCover(false);
  }, [isLoaded, isSignedIn, hintCover]);

  // 2026-09-09 safety valve (same philosophy as BUILD_LOCK_TTL_MS): a dimmed,
  // click-disabled page must never be able to wedge. If neither Clerk's
  // verdict nor the build swap has cleared the cover within 10s, drop it -
  // worst case the stale page is readable while any rebuild continues.
  useEffect(() => {
    if (!hintCover) return;
    const t = window.setTimeout(() => setHintCover(false), 10_000);
    return () => window.clearTimeout(t);
  }, [hintCover]);

  // Fire once: a signed-in visitor on a stale product page (auto R5, or R4
  // after the ribbon signs them in and isSignedIn flips true). The build lock
  // stops a second visit inside the 1-2 minute build window from launching a
  // duplicate paid search for the same page.
  useEffect(() => {
    if (!eligible || !isLoaded || started.current) return;
    if (!isSignedIn) return;
    started.current = true;
    if (!acquireBuildLock(q)) {
      // A rebuild for this page is already running (this browser, e.g. a
      // reload inside the 6-minute lock). 2026-09-16 (leak 3, B-234): attach
      // to it on the server (everything so far, then live) instead of
      // showing the saved page and waiting. attach_only: the server never
      // starts a paid search for this request; with nothing to attach to
      // it answers nothing_to_attach and onDeadEnd restores the saved page.
      setHintCover(false);
      setAttachOnly(true);
      setActive(true);
      return;
    }
    // 2026-09-09 (owner-caught grey-page bug, mirrors CategoryLiveBuild):
    // the dim cover only bridges to the build swap - clear it on
    // activation so the dead-end valve below can never restore the saved
    // page inside a permanently dimmed, click-disabled wrapper.
    setHintCover(false);
    setActive(true);
  }, [eligible, isLoaded, isSignedIn, q]);

  if (!LIVE_PRICES_ENABLED) {
    return <>{children}</>;
  }

  // Hint phase (Clerk still booting): DIM the stale page instead of blanking it
  // (a blank frame reads as a blink). The wrapper div renders always, so the DOM
  // tree is stable across idle -> hint (only the className changes, no remount);
  // clicks are disabled while dimmed. The paid search has NOT started yet.
  if (!active) {
    return (
      <div
        aria-busy={hintCover || undefined}
        className={
          hintCover
            ? "pointer-events-none select-none opacity-40 transition-opacity duration-300"
            : "transition-opacity duration-300"
        }
      >
        {children}
      </div>
    );
  }

  // The full live build, in place: the standard gradual comparison build (the
  // same component /compare renders), ending on the built comparison with no
  // navigation. forceFresh bypasses the stale cache; committedProduct enforces
  // the never-list contract; the completed search republishes this slug so the
  // next visit serves it fresh from cache.
  return (
    <ComparisonStream
      key={q}
      query={q}
      postcode={postcode}
      userId={user?.id ?? null}
      forceFresh
      committedProduct
      seed={seed}
      oneCard={oneCard}
      attachOnly={attachOnly}
      pageSlug={pageSlug}
      // Option A dead-end valve: a rebuild that ends with nothing restores
      // the saved page (children) instead of a not-found surface. `started`
      // stays true, so the build cannot immediately re-fire; the page's next
      // natural visit retries. 2026-09-09: also drop the hint cover -
      // restoring children dimmed + click-disabled is a dead page.
      onDeadEnd={() => {
        setActive(false);
        setHintCover(false);
      }}
    />
  );
}
